// The incoming-call ringtone: public/sounds/incoming-call.wav, a soft
// two-note chime synthesized for Lixani from sine tones (no third-party
// recording, nothing to license). Three seconds, ending in silence, so it
// loops without a click.
//
// Only one Lixani tab rings at a time: every open tab sees the same call, and
// several copies of the loop slightly out of step sound like a fault. A Web
// Lock decides which tab; where the browser has none, every tab rings.
//
// A page may play sound only once its user has interacted with it. A tab that
// has not cannot ring, so it lets the lock go and the other tabs try theirs.

export const RINGTONE_SRC = '/sounds/incoming-call.wav';
const LOCK_NAME = 'lixani-ringtone';
const VOLUME = 0.6;
// A tab that missed the lock tries again once, in case its holder could not
// play.
const RETRY_MS = 500;

let audio = null;
let wanted = false;
let releaseLock = null;
let retryTimer = null;

const audioElement = () => {
  if (!audio) {
    audio = new Audio(RINGTONE_SRC);
    audio.loop = true;
    audio.volume = VOLUME;
  }
  return audio;
};

const release = () => {
  if (releaseLock) {
    releaseLock();
    releaseLock = null;
  }
};

const play = async () => {
  try {
    const element = audioElement();
    element.currentTime = 0;
    await element.play();
    // Stopped while play() was still starting.
    if (!wanted) {
      element.pause();
      return false;
    }
    return true;
  } catch (error) {
    // Autoplay refused (no interaction with this tab yet), or no audio.
    return false;
  }
};

const ringHoldingLock = () =>
  navigator.locks.request(LOCK_NAME, { ifAvailable: true }, (lock) => {
    if (!lock || !wanted) return undefined;
    return new Promise((resolve) => {
      releaseLock = resolve;
      play().then((playing) => {
        if (!playing) release();
      });
    });
  });

/** Start ringing in this tab, unless another tab already is. */
export const startRingtone = () => {
  if (wanted) return;
  wanted = true;

  if (typeof navigator === 'undefined' || !navigator.locks) {
    play();
    return;
  }

  ringHoldingLock().catch(() => {});
  retryTimer = setTimeout(() => {
    retryTimer = null;
    if (wanted && !releaseLock) ringHoldingLock().catch(() => {});
  }, RETRY_MS);
};

/** Stop ringing: answered, dismissed, missed or hung up. */
export const stopRingtone = () => {
  wanted = false;
  if (retryTimer) {
    clearTimeout(retryTimer);
    retryTimer = null;
  }
  if (audio) {
    audio.pause();
    audio.currentTime = 0;
  }
  release();
};
